// Copyright 2016 Sorama B.V.

import { BindingEngine, Disposable, autoinject, computedFrom, observable, transient } from "aurelia-framework";
import { WorkflowModalCatalog } from "catalogs/WorkflowModalCatalog";
import { Range as IRange, StreamSpectrum } from "datatypes/SoramaLocalApi";
import {
    IWorkflowModuleInstance,
    IWorkflowModuleLifetime,
    IWorkflowModuleModalInstance,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { WorkflowPropertyBindingMode } from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { userSetting } from "libraries/WorkflowFramework/UserSettingsAttribute";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";
import { SettingsModalState } from "modules/modals/SettingsModal";
import { UnitParser } from "parsers/UnitParser";
import { IColorMap } from "services/ColorMap/IColorMap";
import { IColorMapService } from "services/ColorMap/IColorMapService";
import { FrequencySelectionService } from "services/FrequencySelection/FrequencySelectionService";
import { IMeasurementPreviewService } from "services/Measurement/IMeasurementPreviewService";
import { NotificationUrgency } from "services/Notification/INotificationService";
import { IUserSettingsService } from "services/UserManagement/IUserSettingsService";
import { Frequency } from "units/Frequency";
import { Pressure } from "units/Pressure";
import { Time } from "units/Time";
import { Unit, UnitValue, WeightedUnit } from "units/Unit";
import { UnitSpectrum } from "units/UnitSpectrum";

@transient()
@autoinject
export class WaterfallModule implements IWorkflowModuleLifetime {
    //#region Constants
    public mininumDesiredDisplayedFrequency = new UnitValue(50, Frequency.hertz);

    // Results from the (default) sample frequency
    // Get the nyquist frequency given the default sample rate
    public maximumDesiredDisplayedFrequency = new UnitValue(1500000 / 32 / 2, Frequency.hertz);
    //#endregion

    //#region User Settings Storage Keys
    private static waterfallColorMapSettingKey = "WaterfallModuleWaterfallColorMap";
    //#endregion

    private streamPromise: Promise<unknown>;
    private subscriptions: Disposable[] = [];

    private bindPromise: Promise<void>;
    private viewActive: boolean;
    private firstSpectrum: boolean;
    private _spectrumColorMap: IColorMap;

    public spectrum: UnitSpectrum;
    @userSetting({ parser: new UnitParser() })
    public selectedUnit: Unit = Pressure.dbASPL;

    @observable @userSetting() public timeAxisLength = 10;

    public timeUnit: Unit = Time.second;
    public frequencyUnit: Unit = Frequency.hertz;
    public mininumDisplayedFrequency: UnitValue = this.mininumDesiredDisplayedFrequency;
    public maximumDisplayedFrequency: UnitValue = this.maximumDesiredDisplayedFrequency;
    public maximumStreamTime = 30;

    public availableUnits: WeightedUnit[];

    private streamerWaterfallSettingsModal: IWorkflowModuleModalInstance<SettingsModalState<WaterfallModule>, void>;

    //#region Workflow Properties
    @userSetting()
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    public isFrequencyRangeLinked: boolean;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    public frequencyRange: IRange;
    //#endregion

    //#region Settings
    @observable({ changeHandler: "frequencyRangeInputChanged" })
    @userSetting()
    public minYAxis: number = this.mininumDisplayedFrequency.value;

    @observable({ changeHandler: "frequencyRangeInputChanged" })
    @userSetting()
    public maxYAxis: number = this.maximumDisplayedFrequency.value;
    moduleInstance: IWorkflowModuleInstance;

    @computedFrom("_spectrumColorMap")
    public get waterfallColorMap(): IColorMap {
        return this._spectrumColorMap;
    }
    public set waterfallColorMap(value: IColorMap) {
        if (!this._spectrumColorMap) {
            // Wait until the colorMap service has defined this property, otherwise the saved value is overwritten
            return;
        }
        this._spectrumColorMap = value;
        this.colorMapService.setDefaultColorMap(WaterfallModule.waterfallColorMapSettingKey, value);
    }

    public visibleTimeAxisStart = -10;
    public visibleTimeAxisEnd = 0;

    //#endregion

    constructor(
        private readonly previewService: IMeasurementPreviewService,
        private readonly colorMapService: IColorMapService,
        private readonly userSettingsService: IUserSettingsService,
        private readonly bindingEngine: BindingEngine,
        private readonly frequencySelectionService: FrequencySelectionService
    ) {}

    public activateModule(moduleInstance: IWorkflowModuleInstance): void {
        this.moduleInstance = moduleInstance;

        this.availableUnits = [Pressure.dbSPL, Pressure.dbASPL, Pressure.dbBSPL, Pressure.dbCSPL];

        const streamMeasurementObserver = this.bindingEngine.propertyObserver(
            this.previewService,
            "isSpectrumAvailable"
        );
        this.subscriptions.push(streamMeasurementObserver.subscribe(this.spectrumAvailableChanged));

        const promises = [
            // Get default colormap
            this.colorMapService.getDefaultColorMap(WaterfallModule.waterfallColorMapSettingKey).then((x) => {
                this._spectrumColorMap = x;
            }),
        ];

        this.bindPromise = Promise.all(promises).then(() => {
            this.viewActive = true;
            if (!this.timeAxisLength) this.timeAxisLength = 10;

            this.timeAxisLengthChanged();
            this.spectrumAvailableChanged();
        });
    }

    public deactivateModule(): void {
        this.bindPromise.then(() => {
            this.viewActive = false;

            while (this.subscriptions.length > 0) this.subscriptions.pop().dispose();
        });
    }

    private spectrumAvailableChanged = () => {
        // Check current status of measurements
        if (this.previewService.isSpectrumAvailable.available && !this.streamPromise) {
            // Start streaming
            this.firstSpectrum = true;
            this.streamPromise = this.beginGetSpectrum();
        } else if (this.previewService.isSpectrumAvailable.reason) {
            this.moduleInstance
                .createNotification()
                .withUrgency(
                    this.previewService.isSpectrumAvailable.inProgress
                        ? NotificationUrgency.Info
                        : NotificationUrgency.Warning
                )
                .withMessage(this.previewService.isSpectrumAvailable.reason)
                .show();
        }
    };

    private timeAxisLengthChanged(): void {
        this.visibleTimeAxisStart = -this.timeAxisLength;
        this.visibleTimeAxisEnd = 0;
    }

    private frequencyRangeChanged = (newRange: IRange) => {
        if (this.isFrequencyRangeLinked && newRange) {
            this.minYAxis = newRange.from;
            this.maxYAxis = newRange.to;
        }
    };

    private frequencyRangeInputChanged = () => {
        this.frequencyRange = <IRange>{
            from: this.minYAxis,
            to: this.maxYAxis,
        };
    };

    private isFrequencyRangeLinkedChanged(): void {
        // Trigger change
        this.frequencyRangeChanged(this.frequencyRange);
    }

    private beginGetSpectrum(): Promise<void> {
        return this.previewService
            .previewSpectrum()
            .then((x) => this.endGetSpectrum(x))
            .catch(() => {
                return this.exitGetSpectrum();
            });
    }

    private endGetSpectrum(spectrum: StreamSpectrum): Promise<void> | void {
        // Remove DC
        spectrum.dataPoints = spectrum.dataPoints.filter(
            (x) =>
                x.x >= this.mininumDesiredDisplayedFrequency.value && x.x <= this.maximumDesiredDisplayedFrequency.value
        );

        const time = new Date().getTime() / 1000.0;
        const unitSpectrum = new UnitSpectrum(Frequency.hertz, Pressure.pascal, spectrum.dataPoints, {
            from: time,
            to: time,
        });

        // Set displayed frequencies correctly
        this.mininumDisplayedFrequency = unitSpectrum.getXUnitValue(0);
        this.maximumDisplayedFrequency = unitSpectrum.getXUnitValue(unitSpectrum.data.length - 1);
        if (this.firstSpectrum) {
            this.firstSpectrum = false;
            this.minYAxis = this.mininumDisplayedFrequency.value;
            this.maxYAxis = this.maximumDisplayedFrequency.value;
        }

        // Set the spectrum which updates the Waterfall Plot
        this.spectrum = unitSpectrum;

        // Request new hologram if needed
        return this.exitGetSpectrum();
    }

    private exitGetSpectrum(): Promise<void> | void {
        if (this.previewService.isSpectrumAvailable.available && this.viewActive) return this.beginGetSpectrum();
        else this.streamPromise = null;
    }

    public linkFrequencyRange(link: boolean): void {
        this.isFrequencyRangeLinked = link;
    }

    public openStreamerWaterfallSettingsModal(): void {
        if (this.streamerWaterfallSettingsModal && this.streamerWaterfallSettingsModal.isOpen) {
            this.streamerWaterfallSettingsModal.cancel();
            this.streamerWaterfallSettingsModal = undefined;
            return;
        }
        this.moduleInstance
            .createModal<SettingsModalState<WaterfallModule>, void>(WorkflowModalCatalog.StreamerWaterfallSettingsModal)
            .withState({ belongsTo: this })
            .whenClosed(() => {
                this.streamerWaterfallSettingsModal = undefined;
            })
            .show()
            .then((x) => {
                this.streamerWaterfallSettingsModal = x;
            });
    }
}
